import { Box, HStack, Spinner, Table, Text, VStack } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import {
  Checkbox,
  ErrorState,
  LoadingState,
  Toggle,
  usePagination,
} from '@mangovoice/mango-ui'

import type { HotelsApi } from '../../api/hotelsApi'
import { useHotelsI18n } from '../../i18n'
import {
  useBulkUpdateExtensionHospitality,
  useListExtensionLines,
  useToggleExtensionHospitality,
} from '../../queries/hooks'
import type { ExtensionLine } from '../../types/rooms'
import {
  formatHospitalityStatus,
  valueOrPlaceholder,
} from '../../utils/display'
import { TableToolbar } from '../core/TableToolbar'

interface ExtensionActivationListProps {
  api: HotelsApi
  pbxId: number
  toast: ToastLike
  canEdit: boolean
}

export const ExtensionActivationList = ({
  api,
  pbxId,
  toast,
  canEdit,
}: ExtensionActivationListProps) => {
  const { text, locale } = useHotelsI18n()
  const extensionsQuery = useListExtensionLines({ api, pbxId })

  const toggleMutation = useToggleExtensionHospitality({
    api,
    pbxId,
    options: {
      onSuccess: (data) => {
        const action = data.hospitality
          ? text('hotels.settings.activated', 'activated')
          : text('hotels.settings.deactivated', 'deactivated')
        toast.create({
          title: text(
            'hotels.settings.roomToggleStatus',
            'Room {{roomNumber}} {{action}}',
            {
              roomNumber: data.number,
              action,
            },
          ),
          type: 'success',
        })
      },
      onError: () => {
        toast.create({
          title: text(
            'hotels.settings.toggleHospitalityFailed',
            'Failed to toggle hospitality',
          ),
          type: 'error',
        })
      },
    },
  })

  const [bulkUpdateInFlight, setBulkUpdateInFlight] = useState<{
    count: number
    hospitality: boolean
  } | null>(null)

  const bulkToggleMutation = useBulkUpdateExtensionHospitality({
    api,
    pbxId,
    options: {
      onMutate: (variables) => {
        setBulkUpdateInFlight({
          count: variables.extensionIds.length,
          hospitality: variables.hospitality,
        })
      },
      onSuccess: (data) => {
        if (data.failed_count > 0) {
          const suffix = data.failed_count === 1 ? '' : 's'
          toast.create({
            title: text(
              'hotels.settings.bulkTogglePartialFailure',
              'Failed to update {{count}} room{{suffix}}',
              {
                count: data.failed_count,
                suffix,
              },
            ),
            type: 'error',
          })
          return
        }

        if (data.updated_count === 0) {
          toast.create({
            title: text(
              'hotels.settings.noRoomUpdatesNeeded',
              'No room activation changes were needed',
            ),
            type: 'info',
          })
          return
        }

        const action = data.hospitality
          ? text('hotels.settings.activated', 'activated')
          : text('hotels.settings.deactivated', 'deactivated')
        const suffix = data.updated_count === 1 ? '' : 's'
        toast.create({
          title: text(
            'hotels.settings.bulkRoomToggleStatus',
            '{{count}} room{{suffix}} {{action}}',
            {
              count: data.updated_count,
              suffix,
              action,
            },
          ),
          type: 'success',
        })
      },
      onError: () => {
        toast.create({
          title: text(
            'hotels.settings.bulkToggleHospitalityFailed',
            'Failed to update room activation',
          ),
          type: 'error',
        })
      },
      onSettled: () => {
        setBulkUpdateInFlight(null)
      },
    },
  })

  const isMutating = toggleMutation.isPending || bulkToggleMutation.isPending

  const handleToggle = useCallback(
    (ext: ExtensionLine) => {
      if (!canEdit) return
      toggleMutation.mutate({
        extensionId: ext.id,
        hospitality: !ext.hospitality,
      })
    },
    [canEdit, toggleMutation],
  )

  const [searchQuery, setSearchQuery] = useState('')

  const allExtensions = useMemo(
    () => (Array.isArray(extensionsQuery.data) ? extensionsQuery.data : []),
    [extensionsQuery.data],
  )

  const filteredExtensions = useMemo(() => {
    if (!searchQuery.trim()) return allExtensions
    const q = searchQuery.toLowerCase().trim()
    return allExtensions.filter(
      (ext) =>
        ext.number?.toString().toLowerCase().includes(q) ||
        ext.name?.toLowerCase().includes(q),
    )
  }, [allExtensions, searchQuery])

  const allFilteredExtensionsAreActive = useMemo(
    () =>
      filteredExtensions.length > 0 &&
      filteredExtensions.every((ext) => ext.hospitality),
    [filteredExtensions],
  )

  const handleBulkToggle = useCallback(
    (details: { checked: boolean }) => {
      if (!canEdit || filteredExtensions.length === 0) return

      const targetHospitality = details.checked === true
      const extensionIds = filteredExtensions
        .filter((ext) => ext.hospitality !== targetHospitality)
        .map((ext) => ext.id)

      if (extensionIds.length === 0) return

      bulkToggleMutation.mutate({
        extensionIds,
        hospitality: targetHospitality,
      })
    },
    [bulkToggleMutation, canEdit, filteredExtensions],
  )

  const bulkToggleDisabled =
    !canEdit || isMutating || filteredExtensions.length === 0
  const isBulkUpdating = bulkToggleMutation.isPending
  const bulkUpdateCount = bulkUpdateInFlight?.count ?? 0
  const bulkUpdateSuffix = bulkUpdateCount === 1 ? '' : 's'
  const bulkUpdateAction = bulkUpdateInFlight?.hospitality
    ? text('hotels.settings.activating', 'activating')
    : text('hotels.settings.deactivating', 'deactivating')

  const { pageSize, setPageSize, currentPage, setCurrentPage, offset, limit } =
    usePagination(
      filteredExtensions.length,
      Math.max(filteredExtensions.length, 1),
    )

  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, setCurrentPage])

  const paginatedExtensions = useMemo(
    () => filteredExtensions.slice(offset, offset + limit),
    [filteredExtensions, offset, limit],
  )

  if (extensionsQuery.isLoading)
    return (
      <LoadingState
        title=""
        size="lg"
        ariaLabel={text('hotels.common.loading', 'Loading')}
      />
    )
  if (extensionsQuery.isError) {
    return (
      <ErrorState
        title={text(
          'hotels.errors.loadExtensions',
          'Failed to load extensions',
        )}
        retryLabel={text('hotels.common.retry', 'Try again')}
        onRetry={() => extensionsQuery.refetch()}
      />
    )
  }

  return (
    <VStack align="stretch" gap={4}>
      <Text fontWeight="semibold">
        {text('hotels.settings.activation', 'Activation')}
      </Text>

      <Box
        borderWidth={1}
        borderColor="border.default"
        borderRadius="lg"
        bg="bg.default"
        overflow="hidden"
      >
        <TableToolbar
          totalCount={allExtensions.length}
          filteredCount={filteredExtensions.length}
          currentPage={currentPage}
          pageSize={pageSize}
          searchQuery={searchQuery}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          onSearchChange={setSearchQuery}
          searchPlaceholder={text(
            'hotels.settings.searchRoomsOrNames',
            'Search by room or name...',
          )}
        />

        <Box position="relative" maxH="500px" overflowY="auto">
          {isBulkUpdating && (
            <Box
              position="absolute"
              inset={0}
              bg="bg.default"
              opacity={0.9}
              zIndex={2}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <HStack
                gap={3}
                px={4}
                py={3}
                borderWidth={1}
                borderColor="border.default"
                borderRadius="md"
                bg="bg.default"
                boxShadow="sm"
              >
                <Spinner size="sm" colorPalette="info" />
                <VStack align="start" gap={0}>
                  <Text fontSize="sm" fontWeight="medium">
                    {text(
                      'hotels.settings.bulkToggleInProgress',
                      'Updating {{count}} room{{suffix}} ({{action}})...',
                      {
                        count: bulkUpdateCount,
                        suffix: bulkUpdateSuffix,
                        action: bulkUpdateAction,
                      },
                    )}
                  </Text>
                  <Text fontSize="xs" color="fg.muted">
                    {text(
                      'hotels.settings.bulkToggleInProgressHint',
                      'This operation can take up to a minute.',
                    )}
                  </Text>
                </VStack>
              </HStack>
            </Box>
          )}

          <Table.Root size="sm" variant="line">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader
                  px={4}
                  py={2}
                  w="220px"
                  position="sticky"
                  top={0}
                  bg="bg.subtle"
                  zIndex={1}
                >
                  <VStack align="start" gap={0}>
                    <HStack gap={1}>
                      <Text fontSize="sm" fontWeight="semibold">
                        {text('hotels.settings.active', 'Active')}
                      </Text>
                      <Toggle
                        checked={allFilteredExtensionsAreActive}
                        onCheckedChange={handleBulkToggle}
                        disabled={bulkToggleDisabled}
                        size="sm"
                        colorPalette="brand"
                        aria-label={text(
                          'hotels.settings.toggleAllActiveAria',
                          'Toggle all rooms active',
                        )}
                      />
                      <Text fontSize="2xs" color="fg.muted" whiteSpace="nowrap">
                        {text(
                          'hotels.settings.enableDisableAll',
                          '(enable/disable all)',
                        )}
                      </Text>
                    </HStack>
                  </VStack>
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  px={4}
                  py={2}
                  position="sticky"
                  top={0}
                  bg="bg.subtle"
                  zIndex={1}
                >
                  {text('hotels.settings.roomNumber', 'Room Number')}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  px={4}
                  py={2}
                  position="sticky"
                  top={0}
                  bg="bg.subtle"
                  zIndex={1}
                >
                  {text('hotels.settings.extensionName', 'Extension Name')}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  px={4}
                  py={2}
                  position="sticky"
                  top={0}
                  bg="bg.subtle"
                  zIndex={1}
                >
                  {text('hotels.settings.currentStatus', 'Current Status')}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paginatedExtensions.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={4} textAlign="center" py={8}>
                    <Text color="fg.muted">
                      {searchQuery
                        ? text(
                            'hotels.settings.noMatchingExtensions',
                            'No extensions match your search',
                          )
                        : text(
                            'hotels.settings.noExtensionsForPbx',
                            'No extension lines found for this PBX',
                          )}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                paginatedExtensions.map((ext) => (
                  <Table.Row key={ext.id}>
                    <Table.Cell px={2} py={3}>
                      <Checkbox
                        checked={ext.hospitality}
                        onCheckedChange={() => handleToggle(ext)}
                        disabled={!canEdit || isMutating}
                        size="sm"
                        inputProps={{
                          'aria-label': text(
                            'hotels.settings.roomActiveAria',
                            'Room {{number}} active',
                            { number: ext.number },
                          ),
                        }}
                      />
                    </Table.Cell>
                    <Table.Cell px={4} py={3}>
                      <Text fontSize="sm">{ext.number}</Text>
                    </Table.Cell>
                    <Table.Cell px={4} py={3}>
                      <Text fontSize="sm">
                        {valueOrPlaceholder(ext.name, text)}
                      </Text>
                    </Table.Cell>
                    <Table.Cell px={4} py={3}>
                      <Text fontSize="sm" color="fg.muted">
                        {formatHospitalityStatus(
                          ext.hospitality_status,
                          locale,
                          text,
                        )}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table.Root>
        </Box>
      </Box>
    </VStack>
  )
}
